/**
 * Kiosk Check-In — touch-optimized self-service
 * Large buttons. Search by name → confirm → mark checked in.
 * Vision spec: Touch-optimized, under 10 seconds, auto-reset.
 */
import { useState, useCallback } from "react";
import { useSearchParams } from "react-router";
import { CheckCircle, Search, AlertTriangle, Loader2, ArrowLeft } from "lucide-react";
import { useDisplayMutation } from "@/hooks/use-display";
import type {
  KioskSearchResult,
  KioskCheckInResult,
  DisplayBookingMatch,
  WalkInQueueJoinResult,
} from "@ease/shared";

type CheckInState = "search" | "results" | "confirm" | "done" | "error";

const DISPLAY_SERVER_URL = import.meta.env.VITE_DISPLAY_SERVER_URL ?? "";
const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export default function KioskCheckInDisplay() {
  const [searchParams] = useSearchParams();
  const displayId = searchParams.get("displayId") ?? "";
  const token = searchParams.get("token") ?? "";
  const joinToken = searchParams.get("joinToken") ?? "";
  const walkInServiceId = searchParams.get("serviceId") ?? "";
  const isWalkInJoin = searchParams.get("mode") === "walk-in" && joinToken.length > 0;

  const [state, setState] = useState<CheckInState>("search");
  const [input, setInput] = useState("");
  const [matches, setMatches] = useState<DisplayBookingMatch[]>([]);
  const [selectedBooking, setSelectedBooking] = useState<DisplayBookingMatch | null>(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [walkInName, setWalkInName] = useState("");
  const [walkInLoading, setWalkInLoading] = useState(false);
  const [walkInError, setWalkInError] = useState<string | null>(null);
  const [walkInResult, setWalkInResult] = useState<WalkInQueueJoinResult | null>(null);
  const [checkInError, setCheckInError] = useState<string | null>(null);

  const checkInMutation = useDisplayMutation<never, KioskCheckInResult>(
    displayId,
    token,
    selectedBooking ? `kiosk/check-in/${selectedBooking.bookingId}` : "",
  );

  const handleSearch = useCallback(async () => {
    if (!input.trim() || !displayId || !token) return;

    const trimmedInput = input.trim();
    setSearchLoading(true);
    setSearchError(null);
    setCheckInError(null);

    if (UUID_REGEX.test(trimmedInput)) {
      try {
        const response = await fetch(`${DISPLAY_SERVER_URL}/display/${displayId}/kiosk/check-in/${trimmedInput}`, {
          method: "POST",
          headers: { "X-Display-Token": token },
          signal: AbortSignal.timeout(10_000),
        });

        if (!response.ok) {
          throw new Error("Check-in failed");
        }

        setState("done");
        setTimeout(() => {
          setState("search");
          setInput("");
          setSelectedBooking(null);
          setMatches([]);
          setSearchError(null);
          setCheckInError(null);
        }, 5000);
      } catch {
        setCheckInError("Booking code not found. Please try your name or check at the front desk.");
        setState("error");
        setTimeout(() => {
          setState("search");
          setInput("");
          setCheckInError(null);
        }, 5000);
      } finally {
        setSearchLoading(false);
      }
      return;
    }

    try {
      const url = `${DISPLAY_SERVER_URL}/display/${displayId}/kiosk/search?q=${encodeURIComponent(trimmedInput)}`;
      const response = await fetch(url, {
        headers: { "X-Display-Token": token },
        signal: AbortSignal.timeout(10_000),
      });

      if (!response.ok) {
        throw new Error("Search failed");
      }

      const json = await response.json() as { data: KioskSearchResult };
      const results = json.data.matches;

      if (results.length === 0) {
        setSearchError("No bookings found. Please check at the front desk.");
        setState("search");
      } else if (results.length === 1) {
        // Single match — go straight to confirm
        setSelectedBooking(results[0] ?? null);
        setState("confirm");
      } else {
        setMatches([...results]);
        setState("results");
      }
    } catch {
      setSearchError("Unable to search. Please try again or check at the front desk.");
    } finally {
      setSearchLoading(false);
    }
  }, [input, displayId, token]);

  const handleWalkInJoin = useCallback(async () => {
    if (!displayId || !joinToken || !DISPLAY_SERVER_URL) {
      return;
    }

    setWalkInLoading(true);
    setWalkInError(null);

    try {
      const response = await fetch(`${DISPLAY_SERVER_URL}/public/walk-in/${displayId}/join`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          joinToken,
          customerName: walkInName.trim() || undefined,
          serviceId: walkInServiceId || undefined,
        }),
        signal: AbortSignal.timeout(10_000),
      });

      if (!response.ok) {
        const body = await response.json().catch(() => ({ error: "Unable to join queue" })) as { error?: string };
        throw new Error(body.error ?? "Unable to join queue");
      }

      const json = await response.json() as { data: WalkInQueueJoinResult };
      setWalkInResult(json.data);
    } catch (caughtError) {
      setWalkInError(caughtError instanceof Error ? caughtError.message : "Unable to join the queue.");
    } finally {
      setWalkInLoading(false);
    }
  }, [displayId, joinToken, walkInName, walkInServiceId]);

  const handleConfirm = useCallback(async () => {
    if (!selectedBooking) return;

    const result = await checkInMutation.mutate();
    if (result?.success) {
      setState("done");
      // Auto-reset after 5 seconds
      setTimeout(() => {
        setState("search");
        setInput("");
        setSelectedBooking(null);
        setMatches([]);
        setSearchError(null);
      }, 5000);
    } else {
      setState("error");
      setTimeout(() => {
        setState("search");
        setInput("");
        setSelectedBooking(null);
      }, 5000);
    }
  }, [selectedBooking, checkInMutation]);

  const resetToSearch = useCallback(() => {
    setState("search");
    setInput("");
    setSelectedBooking(null);
    setMatches([]);
    setSearchError(null);
  }, []);

  if (isWalkInJoin && !displayId) {
    return (
      <div className="min-h-screen bg-[var(--color-background)] flex items-center justify-center p-12">
        <p className="text-2xl text-[var(--color-muted-foreground)]">Queue link is incomplete. Please rescan the QR code.</p>
      </div>
    );
  }

  if (!isWalkInJoin && (!displayId || !token)) {
    return (
      <div className="min-h-screen bg-[var(--color-background)] flex items-center justify-center p-12">
        <p className="text-2xl text-[var(--color-muted-foreground)]">Kiosk not configured. Provide displayId and token.</p>
      </div>
    );
  }

  if (isWalkInJoin) {
    return (
      <div className="min-h-screen bg-[var(--color-background)] flex items-center justify-center p-8">
        <div className="w-full max-w-lg space-y-8 text-center">
          {!walkInResult ? (
            <>
              <div className="space-y-3">
                <h1 className="text-4xl font-bold text-[var(--color-foreground)]">Join the Walk-In Queue</h1>
                <p className="text-xl text-[var(--color-muted-foreground)]">
                  Enter your name and we&apos;ll add you to the live queue.
                </p>
              </div>

              {walkInError && (
                <div className="rounded-xl bg-[var(--color-warning)]/10 border border-[var(--color-warning)]/30 px-6 py-3">
                  <p className="text-lg text-[var(--color-warning)]">{walkInError}</p>
                </div>
              )}

              <input
                type="text"
                value={walkInName}
                onChange={(e) => setWalkInName(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && void handleWalkInJoin()}
                placeholder="Your name"
                autoFocus
                className="w-full rounded-2xl border-2 border-[var(--color-border)] bg-[var(--color-surface)] px-6 py-5 text-2xl text-center text-[var(--color-foreground)] focus:border-[var(--color-primary)] focus:outline-none"
              />
              <button
                type="button"
                onClick={() => { void handleWalkInJoin(); }}
                disabled={walkInLoading}
                className="w-full rounded-2xl bg-[var(--color-primary)] px-8 py-5 text-2xl font-semibold text-white hover:opacity-90 disabled:opacity-50 transition-opacity flex items-center justify-center gap-3"
              >
                {walkInLoading ? (
                  <Loader2 className="h-7 w-7 animate-spin" />
                ) : (
                  <CheckCircle className="h-7 w-7" aria-hidden="true" />
                )}
                Join queue
              </button>
            </>
          ) : (
            <div className="space-y-6">
              <CheckCircle className="h-24 w-24 mx-auto text-[var(--color-success)]" aria-hidden="true" />
              <h1 className="text-4xl font-bold text-[var(--color-success)]">You&apos;re in the queue</h1>
              <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)] p-6">
                <p className="text-sm uppercase tracking-[0.2em] text-[var(--color-muted-foreground)]">Ticket</p>
                <p className="mt-2 text-5xl font-bold text-[var(--color-foreground)]">#{walkInResult.ticketNumber}</p>
                <p className="mt-4 text-lg text-[var(--color-muted-foreground)]">
                  Position {walkInResult.position} · about {walkInResult.estimatedWait} minutes
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--color-background)] flex items-center justify-center p-8">
      <div className="w-full max-w-lg">
        {/* Search state */}
        {state === "search" && (
          <div className="space-y-8 text-center">
            <h1 className="text-4xl font-bold text-[var(--color-foreground)]">Check In</h1>
            <p className="text-xl text-[var(--color-muted-foreground)]">Enter your name or booking code</p>

            {searchError && (
              <div className="rounded-xl bg-[var(--color-warning)]/10 border border-[var(--color-warning)]/30 px-6 py-3">
                <p className="text-lg text-[var(--color-warning)]">{searchError}</p>
              </div>
            )}

            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSearch()}
              placeholder="Your name or code..."
              autoFocus
              className="w-full rounded-2xl border-2 border-[var(--color-border)] bg-[var(--color-surface)] px-6 py-5 text-2xl text-center text-[var(--color-foreground)] focus:border-[var(--color-primary)] focus:outline-none"
            />
            <button
              type="button"
              onClick={handleSearch}
              disabled={!input.trim() || searchLoading}
              className="w-full rounded-2xl bg-[var(--color-primary)] px-8 py-5 text-2xl font-semibold text-white hover:opacity-90 disabled:opacity-50 transition-opacity flex items-center justify-center gap-3"
              aria-label="Search for booking"
            >
              {searchLoading ? (
                <Loader2 className="h-7 w-7 animate-spin" />
              ) : (
                <Search className="h-7 w-7" aria-hidden="true" />
              )}
              Find My Booking
            </button>
          </div>
        )}

        {/* Multiple results state */}
        {state === "results" && (
          <div className="space-y-6">
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={resetToSearch}
                className="rounded-xl border border-[var(--color-border)] p-3 hover:bg-[var(--color-surface)]"
                aria-label="Go back to search"
              >
                <ArrowLeft className="h-6 w-6 text-[var(--color-foreground)]" />
              </button>
              <h1 className="text-3xl font-bold text-[var(--color-foreground)]">Select your booking</h1>
            </div>
            <div className="space-y-3">
              {matches.map((match) => (
                <button
                  key={match.bookingId}
                  type="button"
                  onClick={() => { setSelectedBooking(match); setState("confirm"); }}
                  className="w-full rounded-2xl border-2 border-[var(--color-border)] bg-[var(--color-surface)] p-6 text-left hover:border-[var(--color-primary)]/40 transition-colors"
                >
                  <div className="text-2xl font-semibold text-[var(--color-foreground)]">{match.name}</div>
                  <div className="text-lg text-[var(--color-muted-foreground)]">{match.time} - {match.service}</div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Confirm state */}
        {state === "confirm" && selectedBooking && (
          <div className="space-y-8 text-center">
            <h1 className="text-4xl font-bold text-[var(--color-foreground)]">Is this you?</h1>
            <div className="rounded-2xl border-2 border-[var(--color-primary)] bg-[var(--color-primary)]/5 p-8">
              <div className="text-3xl font-semibold text-[var(--color-foreground)]">{selectedBooking.name}</div>
              <div className="text-xl text-[var(--color-muted-foreground)] mt-2">
                {selectedBooking.time} - {selectedBooking.service}
              </div>
            </div>
            <div className="flex gap-4">
              <button
                type="button"
                onClick={resetToSearch}
                className="flex-1 rounded-2xl border-2 border-[var(--color-border)] px-8 py-5 text-xl font-semibold text-[var(--color-foreground)] hover:bg-[var(--color-surface)]"
              >
                Not Me
              </button>
              <button
                type="button"
                onClick={handleConfirm}
                disabled={checkInMutation.isLoading}
                className="flex-1 rounded-2xl bg-[var(--color-success)] px-8 py-5 text-xl font-semibold text-white hover:opacity-90 disabled:opacity-50 flex items-center justify-center gap-2"
                aria-label="Confirm check in"
              >
                {checkInMutation.isLoading ? (
                  <Loader2 className="h-6 w-6 animate-spin" />
                ) : (
                  <CheckCircle className="h-6 w-6" aria-hidden="true" />
                )}
                Yes, Check In
              </button>
            </div>
          </div>
        )}

        {/* Done state */}
        {state === "done" && (
          <div className="text-center space-y-6">
            <CheckCircle className="h-24 w-24 mx-auto text-[var(--color-success)]" aria-hidden="true" />
            <h1 className="text-4xl font-bold text-[var(--color-success)]">Checked In!</h1>
            <p className="text-xl text-[var(--color-muted-foreground)]">Please have a seat. We&apos;ll call you shortly.</p>
          </div>
        )}

        {/* Error state */}
        {state === "error" && (
          <div className="text-center space-y-6">
            <AlertTriangle className="h-24 w-24 mx-auto text-[var(--color-destructive)]" aria-hidden="true" />
            <h1 className="text-4xl font-bold text-[var(--color-destructive)]">Check-In Failed</h1>
            <p className="text-xl text-[var(--color-muted-foreground)]">
              {checkInError ?? checkInMutation.error ?? "Please check at the front desk."}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
